import React, { useEffect, useRef } from 'react'
import classNames from 'classnames'
import { Input } from 'components/ui'

const sanitize = (text) => (text || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase()

const CodeInput = ({
    length = 6,
    value = '',
    onChange,
    disabled = false,
    autoFocus = false,
    ariaLabel = '',
    className,
}) => {
    const inputs = useRef([])
    const latestValue = useRef(value)
    latestValue.current = value

    const activeIndex = Math.min(value.length, length - 1)

    const focusBox = (index) => {
        inputs.current[Math.max(0, Math.min(index, length - 1))]?.focus()
    }

    useEffect(() => {
        if (autoFocus && !value) focusBox(0)
    }, [autoFocus, value])

    const commit = (nextValue, focusIndex) => {
        latestValue.current = nextValue
        onChange(nextValue)
        focusBox(focusIndex)
    }

    const fill = (index, text) => {
        const chars = sanitize(text)
        if (!chars) return

        const start = chars.length >= length ? 0 : index
        const nextValue = (value.slice(0, start) + chars + value.slice(start + chars.length)).slice(0, length)
        commit(nextValue, start + chars.length)
    }

    const remove = (index, focusIndex = index) => {
        commit(value.slice(0, index) + value.slice(index + 1), focusIndex)
    }

    const onBoxChange = (index) => (event) => {
        let text = event.target.value
        if (text.length === 2 && value[index]) text = text[0] === value[index] ? text[1] : text[0]

        if (text) fill(index, text)
        else remove(index)
    }

    const onBoxKeyDown = (index) => (event) => {
        if (event.key === 'Backspace') {
            event.preventDefault()
            if (value[index]) remove(index)
            else if (index > 0) remove(index - 1)
        } else if (event.key === 'ArrowLeft') {
            event.preventDefault()
            focusBox(index - 1)
        } else if (event.key === 'ArrowRight') {
            event.preventDefault()
            focusBox(Math.min(index + 1, value.length))
        }
    }

    const onBoxPaste = (index) => (event) => {
        event.preventDefault()
        fill(index, event.clipboardData.getData('text'))
    }

    const onBoxFocus = (index) => (event) => {
        const filled = latestValue.current.length
        if (index > filled) {
            focusBox(filled)
            return
        }
        event.target.select()
    }

    return (
        <div className={classNames('flex justify-center gap-2 sm:gap-3', className)}>
            {Array.from({ length }, (_, index) => (
                <Input
                    key={index}
                    ref={(element) => (inputs.current[index] = element)}
                    value={value[index] || ''}
                    onChange={onBoxChange(index)}
                    onKeyDown={onBoxKeyDown(index)}
                    onPaste={onBoxPaste(index)}
                    onFocus={onBoxFocus(index)}
                    tabIndex={index === activeIndex ? 0 : -1}
                    disabled={disabled}
                    aria-label={`${ariaLabel} ${index + 1}`.trim()}
                    autoComplete={index === 0 ? 'one-time-code' : 'off'}
                    autoCapitalize="characters"
                    spellCheck={false}
                    className="w-10 sm:w-12 px-0 text-center text-lg font-semibold bg-gray-100 dark:bg-gray-700 border-transparent dark:border-transparent"
                />
            ))}
        </div>
    )
}

export default CodeInput
